import { ReactElement } from 'react'

import { routes } from '@builddocs/data-management'
import { OperationId } from '@builddocs/data-management'
import { Stack, Typography, Tooltip, Link } from '@mui/material'

interface BillingTooltipModalProps {
  type: OperationId
  count?: number
  children: ReactElement
}

export const IconSvg = () => (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect fill="#4169E1" height="24" rx="4" width="24" />
    <path
      d="M11.9995 20.002C16.4178 20.002 19.9995 16.4202 19.9995 12.002C19.9995 7.58368 16.4178 4.00195 11.9995 4.00195C7.58123 4.00195 3.99951 7.58368 3.99951 12.002C3.99951 16.4202 7.58123 20.002 11.9995 20.002Z"
      fill="white"
    />
    <path
      clipRule="evenodd"
      d="M11.9994 4.60039C7.91251 4.60039 4.59941 7.91348 4.59941 12.0004C4.59941 16.0873 7.91251 19.4004 11.9994 19.4004C16.0863 19.4004 19.3994 16.0873 19.3994 12.0004C19.3994 7.91348 16.0863 4.60039 11.9994 4.60039ZM3.39941 12.0004C3.39941 7.25074 7.24977 3.40039 11.9994 3.40039C16.7491 3.40039 20.5994 7.25074 20.5994 12.0004C20.5994 16.75 16.7491 20.6004 11.9994 20.6004C7.24977 20.6004 3.39941 16.75 3.39941 12.0004Z"
      fill="white"
      fillRule="evenodd"
    />
    <path
      d="M7.59961 14.0004V10.0004L9.99961 11.2004L11.9996 8.40039L13.9996 11.2004L16.3996 10.0004V14.0004H7.59961Z"
      fill="#4169E1"
    />
    <path
      clipRule="evenodd"
      d="M11.9995 7.80078C12.1933 7.80078 12.3751 7.89436 12.4878 8.05204L14.1898 10.4348L16.1312 9.46413C16.3172 9.37113 16.5381 9.38107 16.715 9.49039C16.8918 9.59971 16.9995 9.79283 16.9995 10.0008V14.0008C16.9995 14.3322 16.7309 14.6008 16.3995 14.6008H7.59951C7.26814 14.6008 6.99951 14.3322 6.99951 14.0008V10.0008C6.99951 9.79283 7.10718 9.59971 7.28407 9.49039C7.46096 9.38107 7.68185 9.37113 7.86784 9.46413L9.80927 10.4348L11.5113 8.05204C11.6239 7.89436 11.8057 7.80078 11.9995 7.80078ZM11.9995 9.43306L10.4878 11.5495C10.3161 11.7898 9.99526 11.8695 9.73118 11.7374L8.19951 10.9716V13.4008H15.7995V10.9716L14.2678 11.7374C14.0038 11.8695 13.6829 11.7898 13.5113 11.5495L11.9995 9.43306Z"
      fill="#4169E1"
      fillRule="evenodd"
    />
  </svg>
)

const getPlural = (singular: string, num: number) => {
  let plural = singular + 'ов'
  const firstDigit = num % 10
  const secondDigit = Math.trunc((num % 100) / 10)

  if (secondDigit !== 1) {
    if (firstDigit === 1) {
      plural = singular
    }
    if (firstDigit > 1 && firstDigit <= 4) {
      plural = singular + 'а'
    }
  }

  return plural
}

const getTextTemplate = ({
  count,
}: {
  count: number
}): Record<OperationId, string> => ({
  SEND_DOC_TO_CHECK: `У вас осталось ${count} ${getPlural(
    'акт',
    count,
  )} для отправки на проверку в бесплатном тарифе`,
  CREATE_STAMP: `Вы можете поставить штампы только на ${count} ${getPlural(
    'документ',
    count,
  )} в бесплатном тарифе`,
  BATCH_UPLOAD_DOCS_TO_DOCUMENTARY: `Вы можете совершить массовую загрузку документов ${count} раз в бесплатном тарифе`,
})

export function BillingTooltipModal({
  children,
  type,
  count,
}: BillingTooltipModalProps) {
  if (count === undefined || count === null || count === -1) {
    return children
  }

  const title = getTextTemplate({ count })[type]

  return (
    <Tooltip
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: '#ffffff',
            opacity: 1,
            borderRadius: '8px',
            padding: '8px',
            width: '450px',
            maxWidth: '450px',
            boxShadow: '0px 2px 8px 0px #00000026',
          },
        },
      }}
      title={
        <Stack direction="column" spacing={1}>
          <Typography
            sx={{
              color: '#333333',
              fontSize: '14px',
              lineHeight: '20px',
            }}
          >
            Обратите внимание, есть ограничения
          </Typography>
          <Typography
            sx={{
              color: '#333333',
              fontSize: '12px',
              lineHeight: '16px',
            }}
          >
            {title}
          </Typography>
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            spacing={1}
          >
            <Stack alignItems="center" direction="row" spacing={1}>
              <IconSvg />
              <Typography
                sx={{
                  color: '#777777',
                  fontSize: '12px',
                  lineHeight: '16px',
                }}
              >
                В тарифе PRO действия не ограничены
              </Typography>
            </Stack>
            <Link
              href={routes.tariffs.root()}
              style={{
                color: '#4169E1',
                fontSize: '12px',
                textDecoration: 'underline',
              }}
              target="_blank"
            >
              Подробнее
            </Link>
          </Stack>
        </Stack>
      }
    >
      {children}
    </Tooltip>
  )
}
